// Conference Room Tasks panel — real sq_team_tasks via useTeamTasks/
// createTask/mutateTask, reused verbatim from the Team lane. sq_team_tasks
// has no meeting_id/project_id column (only source_channel_id/
// source_message_id/linked_job_id), so this is honestly the operator's own
// "my tasks" view with the meeting embedded in the title on create — the
// same "embed the origin in the title" convention SquareCodeChannelRoute
// already uses for channel-sourced tasks, since no meeting-shaped FK exists.

import { useState } from 'react'
import { useTeamTasks, createTask, mutateTask } from '../commsData'
import type { TeamTask } from '../commsData'

const MONO = 'var(--sc-font-mono, "JetBrains Mono", ui-monospace, monospace)'

const STATUS_COLOR: Record<TeamTask['status'], string> = {
  open: '#8a93a3', in_progress: '#0891b2', blocked: '#dc2626', done: '#059669',
}

export function TasksPanel({ meetingTitle }: { meetingTitle: string }) {
  const tasks = useTeamTasks()
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const add = async () => {
    const title = draft.trim()
    if (!title || saving) return
    setSaving(true)
    try {
      await createTask({ title: `${title} (from meeting — ${meetingTitle})` })
      setDraft('')
      tasks.reload()
    } catch {
      // Honest no-op — draft stays for retry.
    } finally {
      setSaving(false)
    }
  }

  const toggleDone = async (task: TeamTask) => {
    setBusyId(task.id)
    try {
      await mutateTask('update_status', { id: task.id, status: task.status === 'done' ? 'open' : 'done' })
      tasks.reload()
    } catch {
      // Honest no-op.
    } finally {
      setBusyId(null)
    }
  }

  if (tasks.authRequired) return <p className="cr-state-note">Sign in to read your team tasks.</p>
  if (tasks.error) return <p className="cr-state-note">{tasks.error}</p>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <p style={{ fontSize: 10.5, color: '#5c6675', fontFamily: MONO, lineHeight: 1.6, margin: '0 0 8px' }}>Your real Team Tasks — sq_team_tasks has no meeting-scoped field yet, so this is your task list, not a durable meeting ledger. Tasks created here name this meeting in their title.</p>
      <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gap: 6, alignContent: 'start' }}>
        {tasks.loading && <p className="cr-state-note">Loading tasks…</p>}
        {!tasks.loading && tasks.items.length === 0 && <p className="cr-state-note">No open tasks.</p>}
        {tasks.items.map((t) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 800, color: STATUS_COLOR[t.status], textTransform: 'uppercase', flex: '0 0 auto' }}>{t.status}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#e8ecf2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
            <button type="button" disabled={busyId === t.id} onClick={() => void toggleDone(t)}>{t.status === 'done' ? 'Reopen' : 'Done'}</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void add() }}
          placeholder="Add a task…"
          aria-label="Add a task from this meeting"
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: '#e8ecf2', fontSize: 12.5 }}
        />
        <button type="button" onClick={() => void add()} disabled={saving || !draft.trim()}>Add</button>
      </div>
    </div>
  )
}
